import React from 'react'
import { Controller, useForm, UseFormReturn } from 'react-hook-form'
import FormControl from '@mui/material/FormControl'
import FormLabel from '@mui/material/FormLabel'
import { Theme } from '@mui/system'
// Using require instead of import because react-phone-input-2 is not being rendered when importing from workflows app
// https://stackoverflow.com/questions/34130539/uncaught-error-invariant-violation-element-type-is-invalid-expected-a-string
const PhoneInput = require('react-phone-input-2').default

import { countriesSx } from '../../utils/countries'

import { shouldDisplayError } from '../../utils/formErrorHandlers'

const defaultSx = (theme: Theme) => ({
  phoneInput: {
    ':hover fieldset': { borderColor: 'rgba(0, 0, 0, 0.87)' },
    width: '100%',
    justifyContent: 'center',
    height: theme.spacing(5.25),
    ' .Mui-focused fieldset': {
      padding: '0 7px',
      borderWidth: '2px',
      borderColor: theme.palette.primary.main,
    },
    ' fieldset': {
      bottom: '0',
      boxSizing: 'border-box',
      overflow: 'hidden',
      padding: '0 8px',
      pointerEvents: 'none',
      position: 'absolute',
      top: '0',
      width: '100%',
      marginInlineStart: '0px',
      marginInlineEnd: '0px',
    },
    ' fieldset legend': {
      transform: 'scale(0.75)',
      maxWidth: '100%',
    },
    ' input.form-control': {
      backgroundColor: 'transparent',
      border: 'none',
      height: theme.spacing(5.25),
      lineHeight: 'inherit',
      padding: '13.5px 0px 10.5px 0px',
      marginLeft: '56px',
      width: 'calc(100% - 56px)',
    },
    ' .MuiInputBase-root input.MuiInputBase-input': {
      display: 'none',
    },
    ' .react-tel-input': {
      border: 'none',
      width: '100%',
      ':disabled': {
        cursor: 'not-allowed',
      },

      '.hide': {
        display: 'none',
      },

      '.v-hide': {
        visibility: 'hidden',
      },

      ' .form-control': {
        position: 'relative',
        fontSize: '14px',
        letterSpacing: '.01rem',
        marginTop: '0 !important',
        marginBottom: '0 !important',
        paddingLeft: '48px',
        marginLeft: '0',
        borderRadius: '5px',
        lineHeight: '25px',
        height: theme.spacing(5.25),
        outline: 'none',
      },

      ' .form-control.invalid-number': {
        border: '1px solid #d79f9f',
        backgroundColor: '#FAF0F0',
        borderLeftColor: '#cacaca',
      },

      ' .flag-dropdown': {
        position: 'absolute',
        top: '0',
        bottom: '0',
        padding: '0',
        backgroundColor: '#f5f5f5',
        border: '1px solid #cacaca',
        borderRadius: '3px 0 0 3px',
      },
      ' .form-control.invalid-number:focus': {
        border: '1px solid #d79f9f',
        borderLeftColor: '#cacaca',
        backgroundColor: '#FAF0F0',
      },

      ' .flag-dropdown:hover, .flag-dropdown:focus': {
        cursor: 'pointer',
      },

      ' .flag-dropdown.invalid-number': {
        borderColor: '#d79f9f',
      },

      ' .flag-dropdown.open': {
        zIndex: '2',
        borderRadius: '3px 0 0 0',
      },

      ' .flag-dropdown.open .selected-flag': {
        borderRadius: '3px 0 0 0',
      },

      'input[disabled]+.flag-dropdown:hover': {
        cursor: 'default',
      },

      'input[disabled]+.flag-dropdown:hover .selected-flag': {
        backgroundColor: 'transparent',
      },

      ' .selected-flag': {
        outline: 'none',
        position: 'relative',
        width: '38px',
        height: '100%',
        padding: '0 0 0 8px',
        borderRadius: '3px 0 0 3px',
      },

      '.selected-flag:hover, .selected-flag:focus': {
        backgroundColor: '#fff',
      },

      ' .selected-flag .flag': {
        position: 'absolute',
        top: '50%',
        marginTop: '-5px',
      },

      ' .selected-flag .arrow': {
        position: 'relative',
        top: '50%',
        marginTop: '-2px',
        left: '20px',
        width: '0',
        height: '0',
        borderLeft: '3px solid transparent',
        borderRight: '3px solid transparent',
        borderTop: '4px solid #555',
      },

      ' .selected-flag .arrow.up': {
        borderTop: 'none',
        borderBottom: '4px solid #555',
      },

      ' .country-list': {
        outline: 'none',
        zIndex: '1',
        listStyle: 'none',
        position: 'absolute',
        padding: '0',
        margin: '10px 0 10px -1px',
        boxShadow: '1px 2px 10px rgba(0, 0, 0, 0.35)',
        backgroundColor: 'white',
        width: '300px',
        maxHeight: '200px',
        overflowY: 'scroll',
        borderRadius: '0 0 3px 3px',
      },

      ' .country-list .flag': {
        display: 'inline-block',
        marginRight: '7px',
        marginTop: '2px',
      },

      ' .country-list .divider': {
        paddingBottom: '5px',
        marginBottom: '5px',
        borderBottom: '1px solid #ccc',
      },

      ' .country-list .country': {
        padding: '7px 9px',
      },

      ' .country-list .country .dial-code': {
        color: '#6b6b6b',
      },

      ' .country-list .country:hover': {
        backgroundColor: '#f1f1f1',
      },

      ' .country-list .country.highlight': {
        backgroundColor: '#f1f1f1',
      },
      ' .country-list .country-name': {
        marginRight: '6px',
      },

      ' .country-list .search': {
        position: 'sticky',
        top: '0',
        backgroundColor: '#fff',
        padding: '10px 0 6px 10px',
      },

      ' .country-list .search-emoji': {
        fontSize: '15px',
      },

      ' .country-list .search-box': {
        border: '1px solid #cacaca',
        borderRadius: '3px',
        fontSize: '15px',
        lineHeight: '15px',
        marginLeft: '6px',
        padding: '3px 8px 5px',
        outline: 'none',
      },

      ' .country-list .no-entries-message': {
        padding: '7px 10px 11px',
        opacity: '.7',
      },

      ' .invalid-number-message': {
        position: 'absolute',
        zIndex: '1',
        fontSize: '13px',
        left: '46px',
        top: '-8px',
        background: '#fff',
        padding: '0 2px',
        color: '#de0000',
      },

      ' .special-label': {
        display: 'none',
        position: 'absolute',
        zIndex: '1',
        fontSize: '13px',
        left: '46px',
        top: '-8px',
        background: '#fff',
        padding: '0 2px',
        whiteSpace: 'nowrap',
      },
      ...countriesSx,
    },
    ' .Mui-disabled': {
      pointerEvents: 'none',
    },
    ' .Mui-error fieldset, .Mui-focused.Mui-error fieldset': {
      borderColor: '#d32f2f',
    },
    '.react-tel-input div.flag-dropdown': {
      backgroundColor: 'transparent',
      border: 'none',
      marginLeft: '8px',
      '.open': {
        backgroundColor: 'transparent !important',
      },
      ' .selected-flag': {
        backgroundColor: 'transparent',
        '.open': {
          backgroundColor: 'transparent',
        },
      },
      ' .selected-flag:hover': {
        backgroundColor: 'transparent',
      },
    },
  },
})

//North American Numbering Plan
const NANP_COUNTRY = {
  US: 'us',
  CA: 'ca',
}
const DIAL_CODE_NANP = '1'

interface CountryProps {
  dialCode: string
  countryCode: string
  format: string
  name: string
}

export interface PhoneInputProp {
  country?: string
  disabled?: boolean
  error?: boolean
  form?: UseFormReturn
  id?: string
  inputProps?: object
  label?: string
  name: string
  onBlur?: React.FocusEventHandler
  onKeyDown?: React.KeyboardEventHandler
  onKeyPress?: React.KeyboardEventHandler
  onChange?: Function
  placeholder?: string
  required?: boolean
  rules?: object
  sx?: object
  value?: string
}

const PhoneInputComponent = (props: PhoneInputProp) => {
  const handlerOnBlur = (event: React.FocusEvent, onBlurHook: Function) => {
    setPhoneInputFocused(false)
    onBlurHook(event)
    props.onBlur && props.onBlur(event)
  }
  const handlerOnFocus = (event: React.FocusEvent) => {
    setPhoneInputFocused(true)
  }

  const rules = {
    required: props.required && `${props.label || 'Field'} is required`,
    validate: {
      ...props.rules,
    },
  }

  const useFormDefault = props.form || useForm()
  const { setValue, formState } = useFormDefault
  const { errors } = formState
  const [phoneInputFocused, setPhoneInputFocused] = React.useState(false)

  const handleOnChange = (
    value: string,
    country: CountryProps,
    event: object,
    formatted: string,
  ) => {
    // TODO: This fix may not correctly handle autofill with phone numbers outside of US/CA
    // Replacing react-phone-input-2 with react-phone-number-input may solve the issue
    // https://plastiq.atlassian.net/browse/AP-29368
    const dialCode = country?.dialCode
    const isNANPAutofill =
      value.length === 10 && !value.startsWith(DIAL_CODE_NANP)

    const normalizedValue = isNANPAutofill
      ? `${DIAL_CODE_NANP}${value}`
      : value.startsWith(dialCode)
      ? value
      : `${dialCode}${value}`

    props.onChange && props.onChange(normalizedValue, country, event, formatted)
    setValue(props.name, normalizedValue, {
      shouldValidate: formState.isSubmitted,
    })
  }

  // Controller: Wrapper component to make easier to work with Material-UI external controlled component.
  // React Hook Form embraces uncontrolled components and native inputs.
  // https://blog.logrocket.com/react-hook-form-complete-guide/

  return (
    <Controller
      control={useFormDefault.control}
      defaultValue={props.value}
      name={props.name}
      render={({ field: { onBlur: onBlurHook, value, name, ref } }) => (
        <FormControl
          disabled={props.disabled}
          error={shouldDisplayError(errors, name, props.error)}
          fullWidth
          focused={phoneInputFocused}
          sx={theme => ({
            ...props?.sx,
            ...defaultSx(theme).phoneInput,
          })}
        >
          {/* Wrapping the phoneInput component with a div that gets focus/error from context form */}
          <FormLabel component='div'>
            <PhoneInput
              country={props.country || 'us'}
              inputProps={{
                id: props.id || props.name,
                inputMode: 'tel',
                name: name,
                ref: ref,
              }}
              onBlur={(event: React.FocusEvent) =>
                handlerOnBlur(event, onBlurHook)
              }
              onChange={(
                value: string,
                country: CountryProps,
                event: object,
                formatted: string,
              ) => {
                handleOnChange(value, country, event, formatted)
              }}
              onFocus={(event: React.FocusEvent) => handlerOnFocus(event)}
              placeholder={props.placeholder}
              value={value}
            />
            <fieldset></fieldset>
          </FormLabel>
        </FormControl>
      )}
      rules={rules}
    />
  )
}

export default PhoneInputComponent
